import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { observable } from 'mobx';
import { Alert, Button, Icon, Intent } from '@blueprintjs/core';
import moment from 'moment';
import { Api } from 'services/Api';
import Toast from 'helpers/toaster';
import EditUserPanel, { MOBILE_PERMISSIONS, isOn } from 'components/User/EditUserPanel';
import UserVacations from 'components/User/UserVacations';

const initials = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');

const sentenceCase = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1).replace(/_/g, ' ') : '');

// The last month of work, counted with the same filters the lists open with
// (viewStore.jobsForCustomer etc.), so a tile's number matches the list.
const ACTIVITY = [
  { key: 'jobs', label: 'Jobs', open: 'jobsForCustomer' },
  { key: 'WorkReport', label: 'Work orders', open: 'workReportsForCustomer' },
  { key: 'Quote', label: 'Estimates', open: 'quotesForCustomer' },
  { key: 'Invoice', label: 'Invoices', open: 'invoicesForCustomer' },
];

const countActivity = (userId, key) => {
  const monthAgo = moment().subtract(1, 'month').unix();
  const now = moment().unix();
  const request = key === 'jobs'
    ? Api.post('/jobs/search', {
        search: { where: { start_date: { gte: monthAgo }, end_date: { lte: now }, user_id: userId }, per_page: 1, page: 1 },
      })
    : Api.post('/work_reports/search', {
        search: { where: { created_at: { gte: monthAgo, lte: now }, kind: key, created_by_id: userId }, per_page: 1, page: 1 },
      });
  return request.then((response) => {
    const meta = response && response.data && response.data.meta;
    return meta && typeof meta.total_records === 'number' ? meta.total_records : null;
  });
};

// A team member's page (pages/users/show.js): who they are and how to reach
// them, what they've done this past month, what they can do in the mobile app,
// and their vacations. "Edit details" opens the form in a side panel.
@inject('userStore', 'viewStore') @observer
class UserProfileCard extends Component {
  @observable counts = {}; // activity key -> number, null when it couldn't be counted
  @observable isEditOpen = false;
  @observable isConfirmingDeactivate = false;
  @observable isConfirmingPasswordReset = false;

  componentDidMount() {
    const { user } = this.props;
    ACTIVITY.forEach(({ key }) => {
      countActivity(user.attributes.id, key)
        .then((count) => { this.counts = { ...this.counts, [key]: count }; })
        .catch(() => { this.counts = { ...this.counts, [key]: null }; });
    });
  }

  setActive(active) {
    const { user, userStore } = this.props;
    const name = user.attributes.first_name || user.attributes.full_name;
    this.isConfirmingDeactivate = false;
    const request = active ? userStore.activateUser(user) : userStore.deactivateUser(user);
    request
      .then(() => {
        Toast.show({ message: `${name} ${active ? 'is active again' : 'was deactivated'}.`, intent: Intent.SUCCESS });
      })
      .catch((error) => {
        const reason = error && error.response && error.response.data && error.response.data.errors;
        Toast.show({
          message: reason || `Couldn't ${active ? 'activate' : 'deactivate'} ${name}. Try again.`,
          intent: Intent.DANGER,
        });
      });
  }

  sendPasswordReset() {
    const { user, userStore } = this.props;
    const { first_name: firstName, full_name: fullName, email } = user.attributes;
    this.isConfirmingPasswordReset = false;
    userStore
      .sendPasswordReset(user)
      .then(() => {
        Toast.show({ message: `Password reset email sent to ${email}.`, intent: Intent.SUCCESS });
      })
      .catch((error) => {
        const reason = error && error.response && error.response.data && (error.response.data.error || error.response.data.errors);
        Toast.show({
          message: reason || `Couldn't send ${firstName || fullName} a password reset email. Try again.`,
          intent: Intent.DANGER,
        });
      });
  }

  renderHeader() {
    const { user, userStore, viewStore } = this.props;
    const a = user.attributes;
    const isSelf = viewStore.currentUser && viewStore.currentUser.attributes && viewStore.currentUser.attributes.id === a.id;
    const skills = (a.skills || []).filter(Boolean);
    return (
      <header className="cp-head up-head" aria-label="Team member">
        <span className={`cp-avatar up-avatar${a.is_active ? '' : ' is-inactive'}`} aria-hidden="true">{initials(a.full_name)}</span>
        <div className="cp-head__who">
          <div className="up-head__title">
            <h1 className="cp-head__name">{a.full_name}</h1>
            {!a.is_active && <span className="up-pill up-pill--inactive">Deactivated</span>}
          </div>
          <div className="cp-head__meta">
            {(a.title || a.role) && (
              <span className="up-head__role">
                {a.title}
                {a.role && <span className="up-role">{sentenceCase(a.role)}</span>}
              </span>
            )}
            {a.phone && <a href={`tel:${a.phone}`}><Icon icon="phone" iconSize={12} />{a.phone}</a>}
            {a.email && <a href={`mailto:${a.email}`}><Icon icon="envelope" iconSize={12} />{a.email}</a>}
          </div>
          {skills.length > 0 && (
            <ul className="up-skills" aria-label="Skills">
              {skills.map((skill) => <li key={skill} className="up-skill">{skill}</li>)}
            </ul>
          )}
        </div>
        <div className="up-head__actions">
          <Button className="cp-head__edit" icon="edit" text="Edit details" onClick={() => { this.isEditOpen = true; }} />
          {a.is_active && a.email && (
            <Button
              className="up-button"
              icon="key"
              text="Send password reset"
              loading={userStore.isSendingPasswordReset}
              onClick={() => { this.isConfirmingPasswordReset = true; }}
            />
          )}
          {!isSelf && a.is_active && (
            <Button
              className="up-button up-button--danger"
              icon="disable"
              text="Deactivate"
              loading={userStore.isDeactivateLoading}
              onClick={() => { this.isConfirmingDeactivate = true; }}
            />
          )}
          {!isSelf && !a.is_active && (
            <Button
              className="up-button"
              icon="tick-circle"
              text="Activate"
              loading={userStore.isDeactivateLoading}
              onClick={() => this.setActive(true)}
            />
          )}
        </div>
      </header>
    );
  }

  renderActivity() {
    const { user, viewStore } = this.props;
    return (
      <section className="up-activity" aria-labelledby="up-activity-title">
        <header className="up-activity__head">
          <h2 className="up-card__title" id="up-activity-title">This past month</h2>
          <p className="up-section-hint">Jobs they finished, and the work orders, estimates and invoices they made.</p>
        </header>
        <div className="cp-tabs up-tiles">
          {ACTIVITY.map(({ key, label, open }) => {
            const loaded = key in this.counts;
            const count = this.counts[key];
            return (
              <button
                key={key}
                type="button"
                className="cp-tab up-tile"
                onClick={() => viewStore[open](user.attributes.id)}
                aria-label={loaded && count !== null ? `${label}: ${count}. Open the list` : `${label}. Open the list`}
              >
                <span className="cp-tab__label">{label}</span>
                {loaded ? (
                  <span className="cp-tab__count">{count === null ? '–' : count}</span>
                ) : (
                  <span className="dl-bone cp-bone--count" aria-hidden="true" />
                )}
              </button>
            );
          })}
        </div>
      </section>
    );
  }

  renderMobile() {
    const { user } = this.props;
    const a = user.attributes;
    const allowed = MOBILE_PERMISSIONS.filter(({ key }) => isOn(a[key])).length;
    return (
      <section className="up-card" aria-labelledby="up-mobile-title">
        <header className="up-card__head">
          <h2 className="up-card__title" id="up-mobile-title">Mobile app</h2>
          <span className="up-card__note">{`${allowed} of ${MOBILE_PERMISSIONS.length} allowed`}</span>
        </header>
        <ul className="up-permissions">
          {MOBILE_PERMISSIONS.map(({ key, label }) => {
            const on = isOn(a[key]);
            return (
              <li key={key} className={`up-permission${on ? ' is-on' : ''}`}>
                <Icon icon={on ? 'tick-circle' : 'disable'} iconSize={14} aria-hidden="true" />
                <span className="up-permission__label">{label}</span>
                <span className="sr-only">{on ? 'Allowed' : 'Off'}</span>
              </li>
            );
          })}
        </ul>
        <button type="button" className="up-text-button" onClick={() => { this.isEditOpen = true; }}>
          Change what they can do
        </button>
      </section>
    );
  }

  render() {
    const { user } = this.props;
    const name = user.attributes.first_name || user.attributes.full_name;
    return (
      <div className="cp up">
        {this.renderHeader()}
        {this.renderActivity()}
        <div className="up-columns">
          {this.renderMobile()}
          <UserVacations user={user} />
        </div>

        <EditUserPanel user={user} isOpen={this.isEditOpen} onClose={() => { this.isEditOpen = false; }} />
        <Alert
          isOpen={this.isConfirmingDeactivate}
          icon="disable"
          intent={Intent.DANGER}
          cancelButtonText="Cancel"
          confirmButtonText={`Deactivate ${name}`}
          onCancel={() => { this.isConfirmingDeactivate = false; }}
          onConfirm={() => this.setActive(false)}
        >
          <strong>{`Deactivate ${name}?`}</strong>
          <p>They can't use Field Nexus until they're activated again. You can activate them from this page.</p>
        </Alert>
        <Alert
          isOpen={this.isConfirmingPasswordReset}
          icon="key"
          intent={Intent.PRIMARY}
          cancelButtonText="Cancel"
          confirmButtonText="Send email"
          onCancel={() => { this.isConfirmingPasswordReset = false; }}
          onConfirm={() => this.sendPasswordReset()}
        >
          <strong>{`Send ${name} a password reset email?`}</strong>
          <p>{`It goes to ${user.attributes.email} with a link to set a new password.`}</p>
        </Alert>
      </div>
    );
  }
}

// Shown while the user loads (pages/users/show.js): the page's outline,
// shimmering, instead of a spinner.
export const UserPageSkeleton = () => (
  <div className="cp up cp--loading" role="status" aria-label="Loading team member">
    <div className="cp-head" aria-hidden="true">
      <span className="dl-bone cp-avatar" />
      <div className="cp-head__who">
        <span className="dl-bone" style={{ width: 220, height: 20 }} />
        <span className="dl-bone" style={{ width: 300, height: 12, marginTop: 10 }} />
      </div>
      <span className="dl-bone" style={{ width: 120, height: 36, borderRadius: 8 }} />
    </div>
    <div className="cp-tabs up-tiles" aria-hidden="true">
      {ACTIVITY.map(({ key }) => (
        <span key={key} className="cp-tab cp-tab--loading">
          <span className="dl-bone cp-bone--label" />
          <span className="dl-bone cp-bone--count" />
        </span>
      ))}
    </div>
  </div>
);

export default UserProfileCard;
